import {
  DataTable,
  DataTableColDef,
  DataTableColumnHeader,
} from "@/lib/Components/DataTable/DataTable";
import {
  AllUserListItem,
  allUserListQuery,
} from "@/app/Users/GraphQL/allUserListQuery";
import { ModelLinkCell } from "@/lib/Components/DataTable/Cells/ModelLinkCell";
import { DeEmphasisedCell } from "@/lib/Components/DataTable/DeEmphasisedCell";
import {
  QueryAllUsersOrderByColumn,
  QueryAllUsersOrderByRelationOrderByClause,
  SortOrder,
} from "@/gql/graphql";
import { TimeSinceNow } from "@/lib/Components/Common/TimeSinceNow";
import { UserProfilePicture } from "@/app/Users/Components/UserProfilePicture";
import { Highlighter } from "@/lib/Components/Highlighter";
import { FullPageScreen } from "@/lib/Components/Screens/FullPageScreen";
import { useUserActions } from "@/app/Users/Hooks/useUserActions";

type UserTableColumnId = "user" | "email" | "phone" | "created_at";

export function UserListScreen() {
  "use no memo";

  const getActions = useUserActions();

  const columns: DataTableColDef<AllUserListItem, any, UserTableColumnId>[] = [
    {
      id: "user",
      accessorFn: (s) => s,
      cell: ({ row, table }) => {
        const search = table.options.meta?.search ?? "";
        return (
          <div className="flex items-center space-x-3">
            <UserProfilePicture model={row.original} className="h-8 w-8" />
            <ModelLinkCell
              from="/$tenant/$orgId/supplier/{-$supplierId}"
              to={"/$tenant/$orgId/supplier/{-$supplierId}/users/$id"}
              params={(prev) => ({
                ...prev,
                id: row.original.id,
              })}
              viewTransition
            >
              <Highlighter searchString={search}>
                {row.original.name ?? "--"}
              </Highlighter>
            </ModelLinkCell>
          </div>
        );
      },
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Name" />
      ),
    },
    {
      id: "email",
      accessorFn: (row) => row.primary_email ?? "--",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Email" />
      ),
      cell: DeEmphasisedCell,
    },
    {
      id: "phone",
      accessorFn: (row) => row.primary_phone ?? "--",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Phone" />
      ),
      cell: DeEmphasisedCell,
    },
    {
      id: "created_at",
      accessorFn: (row) => row.created_at,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Created" />
      ),
      cell: ({ row }) => <TimeSinceNow time={row.original.created_at} />,
    },
  ];

  return (
    <FullPageScreen>
      <h3 className="pb-2.5 text-base font-semibold leading-6 text-gray-900">
        Users
      </h3>
      <DataTable
        id="users"
        initialSorting={[{ id: "created_at", desc: true }]}
        columns={columns}
        getActions={getActions}
        getQueryVariables={({ search, pagination, sorting }) => {
          return {
            search,
            page: pagination.pageIndex,
            first: pagination.pageSize,
            orderBy:
              sorting.length && !search
                ? sorting.map((s) => {
                    const order = s.desc ? SortOrder.Desc : SortOrder.Asc;

                    let column: QueryAllUsersOrderByColumn;

                    switch (s.id) {
                      case "user":
                        column = QueryAllUsersOrderByColumn.Name;
                        break;
                      case "created_at":
                        column = QueryAllUsersOrderByColumn.CreatedAt;
                        break;
                      default:
                        column = QueryAllUsersOrderByColumn.Name;
                        break;
                    }

                    return {
                      order,
                      column,
                    } satisfies QueryAllUsersOrderByRelationOrderByClause;
                  })
                : undefined,
          };
        }}
        document={allUserListQuery}
        accessor={(res) => res.allUsers}
      />
    </FullPageScreen>
  );
}
